import { Fragment, useCallback, useEffect, useState, type RefObject } from 'react';
import { AutoFocusInside } from 'react-focus-lock';

import { EPaletteModes, ThemePack } from '@/types/theme-packs.types';
import Container from '@ui/container/container';
import { selectThemePacks } from '@/store/slices/templates/selectors';
import { useAppSelector } from '@/store/hooks';
import Text from '@ui/text/text/text';
import Button from '@ui/buttons/button/button';
import { selectIsSavingWorkingProject, selectSaveId } from '@/store/slices/project/selectors';
import ThemeInfoPillColors from '@/micros/shared-create-and-edit-components/grid-areas/main/project-overview/theme-info-pill/theme-info-pill-colors';
import getThemeFontFamilyClass from '@/utils/fonts/get-theme-font-family-class';
import CustomCarousel from '@ui/custom-carousel/custom-carousel';
import { TCustomCarouselScrollTo } from '@ui/custom-carousel/custom-carousel.types';
import Popover from '@ui/popover/popover';
import { TButtonAndLinkDesigns } from '@styles/design-system/local-components/local-components.types';
import { ICONS } from '@ui/icons/types';
import RotatableIcon from '@ui/RotatableIcon/rotatable-icon';
import styles from './theme-pack-carousel.module.css';
import Icon from '@ui/icons/icon';
import OptionalComponent from '@ui/optional-component/optional-component';
import isGlobalTemplatesActiveChannel from '@ui/auth-wrapper/helpers/is-global-templates-active-channel';
import { disallowedThemePacksEditing } from './global-theme-editing/disallowed-theme-packs-editing';
import GlobalThemeModal from './global-theme-editing/global-theme-modal';
import GlobalTemplatesChannelGuard from '@ui/auth-wrapper/global-templates-channel-guard';
import { selectActiveChannel } from '@/store/slices/user/selectors';
import isGlobalThemePack from '@/utils/is-global-theme-pack';

type TThemePackCarouselProps = {
  activeThemePack?: ThemePack | null;
  themeMode?: EPaletteModes;
  onThemePackChange?: (pack: ThemePack) => void;
  saveId?: string;
  scrollToActiveThemePack?: boolean;
  trayOpen?: boolean;
  modifyTheme: (theme: ThemePack, action: 'edit' | 'duplicate' | 'delete') => void;
  editable?: boolean;
};

const ThemePackCarousel = ({
  activeThemePack,
  themeMode,
  onThemePackChange,
  saveId,
  scrollToActiveThemePack,
  trayOpen,
  modifyTheme,
  editable = true,
}: TThemePackCarouselProps) => {
  const themePacks = useAppSelector(selectThemePacks);
  const savingId = useAppSelector(selectSaveId);
  const savingProject = useAppSelector(selectIsSavingWorkingProject);
  const activeChannel = useAppSelector(selectActiveChannel);

  const [recalcutePosition, setRecalculatePosition] = useState(0);
  const [showMarkAsGlobalModal, setShowMarkAsGlobalModal] = useState<ThemePack | null>(null);

  const handleSelectNewTheme = (selectedThemePack: ThemePack) => {
    onThemePackChange?.(selectedThemePack);
  };

  const handleScrollEnd = useCallback(() => {
    setRecalculatePosition((ticks) => ticks + 1);
  }, []);

  useEffect(() => {
    setRecalculatePosition((ticks) => ticks + 1);
  }, [trayOpen]);

  const scrollToIndex: () => TCustomCarouselScrollTo | undefined = useCallback(() => {
    if (!scrollToActiveThemePack || !themePacks?.length || !activeThemePack) {
      return undefined;
    }
    const index = themePacks.findIndex((pack) => pack.color_pack === activeThemePack.color_pack);
    return {
      index,
      align: 'center',
    };
  }, [activeThemePack, scrollToActiveThemePack, themePacks]);

  const allowedToEditBasedOnChannel = useCallback(
    (themePack: ThemePack) => {
      if (disallowedThemePacksEditing.includes(themePack.title.toLowerCase()) && isGlobalThemePack(themePack)) {
        return false;
      }
      if (isGlobalTemplatesActiveChannel(activeChannel) && isGlobalThemePack(themePack)) {
        return true;
      }
      return themePack.channel > 0;
    },
    [activeChannel]
  );

  if (!activeThemePack || !themePacks || !themeMode) {
    return null;
  }
  return (
    <Container width="100%" height="64px" className="position-relative">
      <CustomCarousel
        virtualized
        justify="center"
        scrollTo={scrollToIndex()}
        list={themePacks.map((themePack) => ({ id: themePack.color_pack, ...themePack }))}
        itemContainerDimensions={{ width: 'fit-content' }}
        onScrollEnd={handleScrollEnd}
        renderItem={(themePack, idx) => {
          const isActive = activeThemePack?.color_pack === themePack.color_pack;
          const portalEl: RefObject<HTMLDivElement | null> = { current: null };
          const canEditThemeBasedOnChannel = allowedToEditBasedOnChannel(themePack);
          const ButtonWrapper = idx === 0 ? AutoFocusInside : Fragment;
          return (
            <div className={styles.themeButton}>
              <ButtonWrapper>
                <Button
                  key={themePack.color_pack}
                  value={themePack.color_pack}
                  backgroundColor={isActive ? 'var(--gray-100)' : 'var(--gray-800)'}
                  hoverBackgroundColor="var(--gray-100)"
                  loading={savingId === saveId && isActive}
                  className={`clear-button text-left padding-2 radius-10 overflow-hidden theme-pack-carousel-item`}
                  width="100%"
                  maxWidth="112px"
                  minWidth="112px"
                  disabled={savingProject}
                  disabledOpacity={1}
                  onKeyDown={(e) => {
                    if (savingProject) return;
                    if ((e.key === 'Enter' || e.key === ' ') && !isActive) {
                      handleSelectNewTheme(themePack);
                    }
                  }}
                  onClick={() => {
                    if (themePack.color_pack !== activeThemePack?.color_pack) {
                      handleSelectNewTheme(themePack);
                    }
                  }}
                >
                  <Container
                    background="var(--gray-800)"
                    className="pointer-events-none padding-6 overflow-hidden flex-column gap-8"
                    height="100%"
                    width="100%"
                    borderRadius="8px"
                  >
                    <ThemeInfoPillColors
                      themeColors={themePack?.colors?.[themeMode]}
                      backgroundDimensions={{ height: '24px', width: '60px' }}
                      accentDimensions={{ height: '24px', width: '32px' }}
                    />
                    <Text
                      textAlign="center"
                      clamp={1}
                      className={getThemeFontFamilyClass(themePack.font_pack?.font_pack)}
                      fontFamily="var(--theme-font-head)"
                      lineHeight="16px"
                      color={isActive ? 'var(--gray-100)' : 'var(--gray-300)'}
                      fontSize={'12px'}
                    >
                      {themePack.title}
                    </Text>
                  </Container>
                </Button>
              </ButtonWrapper>
              <OptionalComponent
                display={(isGlobalTemplatesActiveChannel(activeChannel) || editable) && canEditThemeBasedOnChannel}
              >
                <Popover
                  closeOnClick
                  width="120px"
                  height="96px"
                  forcePosition="bottom-center"
                  open={showMarkAsGlobalModal?.color_pack === themePack.color_pack || undefined}
                  portalEl={portalEl}
                  ignoreClasses={[`alter-theme-${themePack.id}`]}
                  closeOnTabAway
                  cycleWithArrowKeys
                  refocusTriggerOnClose
                  recalcutePosition={recalcutePosition}
                  renderTriggerButton={({ open }) => (
                    <Button
                      className={`flex fd-row jc-center ai-center radius-full padding-0 top-4 right-4 z-2 ${styles.popoverTrigger} ${open ? styles['popover-open'] : ''}`}
                      width="24px"
                      height="24px"
                      design={TButtonAndLinkDesigns.LocalBttnSec}
                      fixedState={open ? 'hover' : undefined}
                      position="absolute"
                      border="1px solid var(--global-opacity-white-10-percent)"
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                    >
                      <RotatableIcon rotate={'90deg'} size={14} name={ICONS.THREE_DOTS_HORIZONTAL} />
                    </Button>
                  )}
                >
                  {({ triggerBounds }) => {
                    return (
                      <Container
                        background={'var(--gray-800)'}
                        className={`position-fixed z-20 overflow-y-auto alter-theme alter-theme-${themePack.id}`}
                        borderRadius="12px"
                        style={{
                          top: triggerBounds?.top || 0,
                          left: (triggerBounds?.left || 0) - (triggerBounds?.width || 0) - 65,
                          transform: 'translateY(-100%)',
                          minWidth: 'max-content',
                        }}
                      >
                        <GlobalTemplatesChannelGuard>
                          <Button
                            disabledOpacity={0.6}
                            className="radius-0 text-left padding-v-10 padding-h-16 width-100-percent flex ai-center gap-10 alter-theme"
                            hoverBackgroundColor="var(--gray-700)"
                            onClick={() => {
                              if (!canEditThemeBasedOnChannel) return;
                              setShowMarkAsGlobalModal(themePack); // show modal
                            }}
                          >
                            <Icon name={ICONS.GLOBE} size={16} />
                            <Text color="var(--gray-100)" className="P2">
                              Mark as global
                            </Text>
                          </Button>
                        </GlobalTemplatesChannelGuard>

                        <Button
                          hoverBackgroundColor="var(--gray-700)"
                          outlineOnFocus={false}
                          disabledOpacity={0.6}
                          disabled={isGlobalThemePack(themePack)}
                          className="radius-0 text-left padding-v-10 padding-h-16 width-100-percent flex ai-center gap-10 alter-theme"
                          onClick={() => {
                            modifyTheme(themePack, 'edit');
                          }}
                        >
                          <Icon name={ICONS.EDIT} size={16} />
                          <Text color="var(--gray-100)" className="P2">
                            Edit name
                          </Text>
                        </Button>
                        <Button
                          hoverBackgroundColor="var(--gray-700)"
                          outlineOnFocus={false}
                          disabledOpacity={0.6}
                          className="radius-0 text-left padding-v-10 padding-h-16 width-100-percent flex ai-center gap-10 alter-theme"
                          onClick={() => {
                            modifyTheme(themePack, 'duplicate');
                          }}
                        >
                          <Icon name={ICONS.COPY} size={16} />
                          <Text color="var(--gray-100)" className="P2">
                            Duplicate
                          </Text>
                        </Button>
                        <Button
                          hoverBackgroundColor="var(--gray-700)"
                          disabledOpacity={0.6}
                          disabled={isGlobalThemePack(themePack)}
                          outlineOnFocus={false}
                          style={{
                            color: 'var(--global-error-400)',
                          }}
                          className="radius-0 text-left padding-v-10 padding-h-16 width-100-percent flex ai-center gap-10 alter-theme"
                          onClick={() => {
                            modifyTheme(themePack, 'delete');
                          }}
                        >
                          <Icon name={ICONS.TRASH} size={16} />
                          <Text color="var(--global-error-400)" className="P2">
                            Delete
                          </Text>
                        </Button>
                      </Container>
                    );
                  }}
                </Popover>
              </OptionalComponent>
            </div>
          );
        }}
      />

      <GlobalTemplatesChannelGuard>
        <GlobalThemeModal setShowMarkAsGlobalModal={setShowMarkAsGlobalModal} themePack={showMarkAsGlobalModal} />
      </GlobalTemplatesChannelGuard>
    </Container>
  );
};

export default ThemePackCarousel;
